import React, { FormEvent, useCallback, useMemo } from 'react';
import './CouponPanel.scss';

import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { mutate } from 'swr';
import { Coupon } from '../../types/coupon';
import { FormField } from '../FormField/FormField';
import { useForm } from '../../hooks/useForm';
import { RequestFeedbackMessages, useClient } from '../../hooks/useClient';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { Panel, ResourcePanelImplProps } from '../Panel/Panel';
import { COUPON_FORM_VALIDATORS, CouponForm } from './validators';
import { getCouponDeleteConfirmationDialogProps } from '../../pages/Coupons/Coupons';

const getToastMessages = (isEditing: boolean): RequestFeedbackMessages => {
  return {
    successMessage: {
      summary: isEditing ? 'Bono modificado' : 'Bono creado',
      detail: isEditing ? 'El bono se ha modificado correctamente' : 'Se ha creado el nuevo bono',
    },
    errorMessages: {
      summary: isEditing ? 'Error modificando bono' : 'Error al crear bono',
      defaultDetail: isEditing ? 'No se ha podido modificar el bono' : 'No se ha podido crear el bono',
      RESOURCE_WITH_SAME_NAME_EXISTS: 'Ya existe un bono con ese nombre',
    },
  };
};

const LOCKED_HELPER = 'No se puede cambiar: el bono ya está asignado a clientes';

const getInitialCouponForm = (coupon: Coupon | undefined): CouponForm => {
  if (!coupon) return { name: '', price: undefined, usageLimit: undefined };
  return {
    name: coupon.name,
    price: coupon.price,
    usageLimit: coupon.usageLimit,
  };
};

export const CouponPanel = ({
  resource,
  visible,
  onHide,
  onIsEditingChange,
  onDeleteResource,
  isEditing = false,
  isDeleting = false,
}: ResourcePanelImplProps<Coupon>) => {
  const { organization } = useOrganizationContext();
  const { post, patch } = useClient();
  const isCreating = !resource;
  const isLocked = !!resource?.isLocked;

  const initialForm = useMemo(() => getInitialCouponForm(resource), [resource]);
  const { form, setFormFields, isSaving, setIsSaving, hasChanged, validationErrors, validate, resetForm } = useForm<CouponForm>(
    initialForm,
    !visible,
    COUPON_FORM_VALIDATORS,
  );

  const handleSave = useCallback(
    async (e?: FormEvent): Promise<boolean> => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newErrors = validate();
      if (!organization || !form.name || form.price === undefined || form.usageLimit === undefined || newErrors) {
        return false;
      }

      setIsSaving(true);
      let response = null;

      const { successMessage, errorMessages } = getToastMessages(isEditing);

      if (isCreating) {
        response = await post<Coupon>(`/coupons`, {
          body: {
            name: form.name,
            price: form.price,
            usageLimit: form.usageLimit,
            organizationId: organization.id,
          },
          successMessage,
          errorMessages,
        });
      } else if (resource) {
        response = await patch<Coupon>(`/coupons/${resource.id}`, {
          body: {
            name: form.name,
            price: form.price,
            usageLimit: form.usageLimit,
          },
          successMessage,
          errorMessages,
        });
      }

      if (response) {
        mutate(`/organizations/${organization.id}/coupons`);
        // The table reads `.../coupons/paginated?page=…` (Coupons.tsx, useServerTable):
        // revalidate it, whatever page, sort or filter it shows (#73).
        const tableKey = `/organizations/${organization.id}/coupons/paginated?`;
        mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
      }

      setIsSaving(false);
      return !!response;
    },
    [validate, organization, form.name, form.price, form.usageLimit, setIsSaving, isEditing, isCreating, resource, post, patch],
  );

  return (
    <Panel
      panelType='resourcePanel'
      resourceName='bono'
      visible={visible}
      onHide={onHide}
      onDelete={() => onDeleteResource(resource?.id)}
      isDeleting={isDeleting}
      onSave={handleSave}
      onIsEditingChange={onIsEditingChange}
      hasChanged={hasChanged}
      isSaving={isSaving}
      isEditing={isEditing}
      isCreating={isCreating}
      resetForm={resetForm}
      customDeleteConfirmationDialogProps={getCouponDeleteConfirmationDialogProps(resource)}
    >
      {!isCreating && !isEditing && (
        <div className='readonly-form'>
          <FormField labelTitle='Nombre' fullWidth>
            <p>{resource?.name}</p>
          </FormField>
          <FormField labelTitle='Precio' fullWidth>
            <p>{resource?.price}€</p>
          </FormField>
          <FormField labelTitle='Usos' fullWidth>
            <p>{resource?.usageLimit}</p>
          </FormField>
        </div>
      )}

      {(isEditing || isCreating) && (
        <form
          className={classNames({
            'form-loading': isSaving,
          })}
          onSubmit={handleSave}
          noValidate
          autoComplete='off'
        >
          <FormField labelTitle='Nombre' elementId='name' error={validationErrors?.name} required>
            <InputText
              id='name'
              placeholder='Ej: Bono de 10 sesiones'
              className={classNames({ 'p-invalid': validationErrors?.name })}
              value={form?.name}
              onChange={(e) => setFormFields({ name: e.target.value })}
            />
          </FormField>

          <FormField
            labelTitle='Precio'
            elementId='price'
            error={validationErrors?.price}
            helper={isLocked ? LOCKED_HELPER : undefined}
            required
          >
            <div className='p-inputgroup'>
              <InputNumber
                id='price'
                placeholder='Ej: 200'
                className={classNames({ 'p-invalid': validationErrors?.price })}
                value={form?.price ?? null}
                onChange={(e) => setFormFields({ price: e.value ?? undefined })}
                disabled={isLocked}
              />
              <span className='p-inputgroup-addon'>€</span>
            </div>
          </FormField>

          <FormField
            labelTitle='Usos'
            elementId='usageLimit'
            error={validationErrors?.usageLimit}
            helper={isLocked ? LOCKED_HELPER : undefined}
            required
          >
            <InputNumber
              id='usageLimit'
              placeholder='Ej: 10'
              className={classNames({ 'p-invalid': validationErrors?.usageLimit })}
              value={form?.usageLimit ?? null}
              onChange={(e) => setFormFields({ usageLimit: e.value ?? undefined })}
              disabled={isLocked}
              min={1}
              showButtons
            />
          </FormField>
        </form>
      )}
    </Panel>
  );
};
